import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { convertLeadToTransaction } from '../../services/crmConvert';
import { crmErrorMessage, emptyAddress, personDisplayName } from '../../utils/crm';
import { defaultTransactionName, sideNoun } from '../../utils/crmPipeline';

/**
 * Creates a transaction for a lead. The result is a Prospect, exactly like one made by hand, with
 * the client added as a reference Buyer or Seller. Nobody is invited and nothing is sent: the
 * agent invites people from the transaction when they choose to.
 */
const ConvertDialog = ({ book, ownerUserId, person, side, onClose, onOpenTransaction, onConverted }) => {
  const displayName = personDisplayName(person);
  const [name, setName] = useState(defaultTransactionName(person, side, displayName));
  const [address, setAddress] = useState({ ...emptyAddress(), ...(side === 'SELL' ? person.propertyAddress || {} : {}) });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [createdId, setCreatedId] = useState(null);
  const [result, setResult] = useState(null);

  const setField = (field, value) => setAddress((a) => ({ ...a, [field]: value }));

  const submit = async (event) => {
    event.preventDefault();
    if (!name.trim()) {
      setError('Give the transaction a name.');
      return;
    }
    setBusy(true);
    setError('');
    try {
      const done = await convertLeadToTransaction({
        book,
        ownerUserId,
        person,
        side,
        name: name.trim(),
        address,
        displayName,
        existingTransactionId: createdId || undefined,
      });
      setResult(done);
      onConverted();
    } catch (err) {
      if (err.transactionId) {
        setCreatedId(err.transactionId);
        setError('The transaction was created, but it could not be linked to this lead yet. Try again; nothing will be created twice.');
      } else {
        setError(err?.response?.status === 400 || err?.response?.status === 409 ? crmErrorMessage(err) : 'The transaction could not be created. Please try again.');
      }
    } finally {
      setBusy(false);
    }
  };

  return createPortal(
    <div className="crm-modal-backdrop crm-modal-top">
      <form className="crm-modal" role="dialog" aria-modal="true" aria-label="Convert to transaction" onSubmit={submit}>
        <h2>Convert to a transaction</h2>
        {result ? (
          <>
            <p>The transaction was created and {displayName} is now a Won {sideNoun(side)}.</p>
            {!result.partyAdded && (
              <p className="crm-error" role="alert">
                {displayName} could not be added as a {sideNoun(side)} on the transaction. Add them from the Parties tab.
              </p>
            )}
            <div className="crm-modal-actions">
              <button type="button" className="crm-btn" onClick={onClose}>Close</button>
              <button type="button" className="crm-btn crm-btn-primary" onClick={() => onOpenTransaction(result.transactionId)}>Open transaction</button>
            </div>
          </>
        ) : (
          <>
            <p className="crm-note">
              This creates a Prospect transaction for {displayName} as a {sideNoun(side)}, adds them as a reference {sideNoun(side)}
              {' '}(only their name, email and phone), and marks the lead Won. Nobody is invited or emailed.
            </p>
            <label>Transaction name
              <input className="crm-input" value={name} onChange={(e) => setName(e.target.value)} maxLength={200} disabled={!!createdId} />
            </label>
            <fieldset className="crm-fieldset" disabled={!!createdId}>
              <legend>Property (optional)</legend>
              <label>Street address
                <input className="crm-input" value={address.line1} onChange={(e) => setField('line1', e.target.value)} maxLength={200} />
              </label>
              <div className="crm-form-grid">
                <label>City
                  <input className="crm-input" value={address.city} onChange={(e) => setField('city', e.target.value)} maxLength={100} />
                </label>
                <label>State
                  <input className="crm-input" value={address.state} onChange={(e) => setField('state', e.target.value)} maxLength={100} />
                </label>
                <label>ZIP code
                  <input className="crm-input" value={address.zip} onChange={(e) => setField('zip', e.target.value)} maxLength={20} />
                </label>
              </div>
            </fieldset>
            {error && <p className="crm-error" role="alert">{error}</p>}
            <div className="crm-modal-actions">
              <button type="button" className="crm-btn" onClick={onClose} disabled={busy}>Cancel</button>
              <button type="submit" className="crm-btn crm-btn-primary" disabled={busy}>
                {busy ? 'Working…' : createdId ? 'Finish linking' : 'Create transaction'}
              </button>
            </div>
          </>
        )}
      </form>
    </div>,
    document.body
  );
};

export default ConvertDialog;
